import { Dispatch, SetStateAction } from 'react'

import {
  DISTRIBUTION_SEARCH_CONDITION_CLEAR,
  DistributionSearchCondition
} from '@/common/api/modules/recommendationDistribution'
import { isSameSearchCondition } from '@/common/api/search'
import { distributionStatusName } from '@/common/app/modules/recommend/recommendationDistribution'
import { RecommendationDistributionStatus } from '@/common/model/recommend/recommendationDistribution'
import { WidgetContentType } from '@/common/model/recommend/widget'

import Button from '@/components/view/atoms/button/Button'
import TextField, { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import SearchConditionAreaContent from '@/components/view/layouts/area/SearchConditionAreaContent'
import SortButtonList from '@/components/view/layouts/button/SortButtonList'
import FormRow from '@/components/view/layouts/form/FormRow'
import DisclosureSortDateBetweenButton from '@/components/view/molecules/disclosure/DisclosureSortDateBetweenButton'
import DisclosureSortRadioSelect from '@/components/view/molecules/disclosure/DisclosureSortRadioSelect'

interface Props {
  condition: DistributionSearchCondition
  setCondition: Dispatch<SetStateAction<DistributionSearchCondition>>
}

const RecommendationDistributionSearchField = ({ condition, setCondition }: Props) => {
  const isDefault = isSameSearchCondition(condition, DISTRIBUTION_SEARCH_CONDITION_CLEAR)
  const dateQuantity = [condition.distributesFrom, condition.distributesTo].filter(Boolean).length

  return (
    <SearchConditionAreaContent>
      <FormRow gap={8} size={TextBoxSize.FULL} align='center'>
        <TextField
          type='search'
          size={TextBoxSize.FULL}
          value={condition.keyword ?? ''}
          onChange={(e) =>
            setCondition((c) => ({ ...c, page: 1, keyword: e.target.value || null }))
          }
          placeholder='配信ID・配信管理名で検索...'
        />
      </FormRow>
      <SortButtonList>
        <DisclosureSortRadioSelect
          name='contentType'
          currentValue={condition.contentType}
          choices={[
            { name: 'すべて', value: null },
            { name: '商品レコメンド', value: WidgetContentType.PRODUCT },
            { name: '記事レコメンド', value: WidgetContentType.ARTICLE }
          ]}
          setCondition={setCondition}
        >
          コンテンツタイプ
        </DisclosureSortRadioSelect>
        <DisclosureSortRadioSelect
          name='status'
          currentValue={condition.status}
          choices={[
            { name: 'すべて', value: null },
            ...Object.values(RecommendationDistributionStatus).map((value) => ({
              name: distributionStatusName(value),
              value
            }))
          ]}
          setCondition={setCondition}
        >
          ステータス
        </DisclosureSortRadioSelect>
        <DisclosureSortRadioSelect
          name='abEnabled'
          currentValue={condition.abEnabled}
          choices={[
            { name: 'すべて', value: null },
            { name: 'あり', value: true },
            { name: 'なし', value: false }
          ]}
          setCondition={setCondition}
        >
          A/Bテスト
        </DisclosureSortRadioSelect>
        <DisclosureSortDateBetweenButton
          quantity={dateQuantity}
          defaultStartValue={condition.distributesFrom}
          defaultEndValue={condition.distributesTo}
          onComplete={(start, end) =>
            setCondition((c) => ({
              ...c,
              page: 1,
              distributesFrom: start || null,
              distributesTo: end || null
            }))
          }
        >
          配信開始
        </DisclosureSortDateBetweenButton>
        <Button
          color='secondary'
          disabled={isDefault}
          onClick={() => setCondition(DISTRIBUTION_SEARCH_CONDITION_CLEAR)}
        >
          条件をリセット
        </Button>
      </SortButtonList>
    </SearchConditionAreaContent>
  )
}

export default RecommendationDistributionSearchField
